/**
 * Agent Configuration Page
 * Edit agent configuration with tabbed interface
 *
 * New Tab Structure (2024-12):
 * - Basic: Core settings (name, model, LLM params)
 * - Hooks: Feature toggles + Hooks pipeline
 * - Memory: Memory system config (conditional)
 * - Classifier: Response classifier + Skills (conditional)
 * - Team: Team mode config (conditional)
 * - Tools: Tool configuration
 * - Prompts: System prompts + Templates
 * - Advanced: Advanced settings
 * - Schemas: Output schemas (conditional)
 * - JSON: JSON preview
 * - Versions: Version history
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useAgentConfigQuery,
  useUpdateAgentMutation,
  useAllModelsQuery,
  useAgentVersionsQuery,
} from '@/lib/queries';
import { Loading, ErrorMessage } from '@/components/common';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { FieldSection } from '@/components/fields/FieldSection';
import { HooksPipeline } from '@/features/hooks/HooksPipeline';
import { ToolsBrowser } from '@/features/tools/components/ToolsBrowser';
import { MCPToolsBrowser } from '@/features/tools/components/MCPToolsBrowser';
import {
  VersionHistory,
  CreateAgentDialog,
  OutputSchemasTab,
  TemplateSelector,
  AgentPromptPreview,
  BasicTab,
  MemoryTab,
  KnowledgeTab,
  AstrologyTab,
  EmotionTab,
  ClassifierTab,
  TeamTab,
  AdvancedTab,
  ToolComboSection,
} from '@/features/agent-config/components';
import { FIELD_CONFIG, getConfigValue } from '@/config/fieldConfig';
import { FIELD_SECTIONS } from '@/config/fieldSections';
import { getVisibleTabs, getInitialExpandedTabs, type TabId } from '@/config/tabConfig';
import { useI18n } from '@/i18n';
import type { AgentConfiguration, ToolMode } from '@/types';
import {
  ArrowLeft,
  Copy,
  History,
  Settings,
  Wrench,
  Sparkles,
  FileJson,
  FileText,
  Brain,
  Filter,
  Users,
  Cog,
  BookOpen,
  Star,
  Heart,
  Eye,
  X,
  AlertTriangle,
} from 'lucide-react';

type BrowserMode = ToolMode | 'mcp';

// Mapping from config paths to expandable tabs (for auto-expand on enable)
const FEATURE_PATH_TO_TAB: Record<string, TabId> = {
  'memory.enabled': 'memory',
  'knowledge_enabled': 'knowledge',
  'hooks.astrology_plus.enabled': 'astrology',
  'hooks.emotion_enhance.enabled': 'emotion',
  'hooks.classifier.enabled': 'classifier',
};

// Mapping from tab ID to feature config (for collapse confirmation)
const TAB_TO_FEATURE_CONFIG: Record<TabId, { path: string; name: string } | null> = {
  basic: null,
  hooks: null,
  memory: { path: 'memory.enabled', name: '记忆系统' },
  knowledge: { path: 'knowledge_enabled', name: '知识库' },
  astrology: { path: 'hooks.astrology_plus.enabled', name: '占星功能' },
  emotion: { path: 'hooks.emotion_enhance.enabled', name: '情绪感知' },
  classifier: { path: 'hooks.classifier.enabled', name: '响应分类器' },
  team: { path: 'agent_mode', name: '团队模式' },
  tools: null,
  prompts: null,
  advanced: null,
  schemas: null,
  versions: null,
};

// Mapping from tabId to hook name for hooks_visible sync when collapsing
const TAB_TO_HOOK_NAME: Record<string, string | null> = {
  knowledge: 'knowledge_context',
  astrology: 'astrology_plus',
  emotion: 'emotion_enhance',
  classifier: 'classifier',
};

// Icon mapping for tabs
const TAB_ICONS: Record<TabId, React.ComponentType<{ className?: string }>> = {
  basic: Settings,
  hooks: Sparkles,
  memory: Brain,
  knowledge: BookOpen,
  astrology: Star,
  emotion: Heart,
  classifier: Filter,
  team: Users,
  tools: Wrench,
  prompts: FileText,
  advanced: Cog,
  schemas: FileJson,
  versions: History,
};

export const AgentConfigPage: React.FC = () => {
  const { agentType } = useParams<{ agentType: string }>();
  const navigate = useNavigate();
  const { messages, language } = useI18n();
  const t = messages.agentConfig;
  const tc = messages.common;
  const ta = messages.actions;
  const [activeTab, setActiveTab] = useState<TabId>('basic');
  const [formData, setFormData] = useState<Partial<AgentConfiguration>>({});
  const [isDirty, setIsDirty] = useState(false);
  // Expanded tabs state - controls which feature tabs are visible (扩展启用)
  const [expandedTabs, setExpandedTabs] = useState<Set<TabId>>(new Set());

  // Tool browser state
  const [toolBrowserOpen, setToolBrowserOpen] = useState(false);
  const [toolBrowserMode, setToolBrowserMode] = useState<BrowserMode>('custom');
  const [toolBrowserField, setToolBrowserField] = useState<string>('');

  // Copy dialog state
  const [showCopyDialog, setShowCopyDialog] = useState(false);

  // Prompt preview dialog state
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  // Collapse confirmation dialog state
  const [collapseConfirmation, setCollapseConfirmation] = useState<{
    tabId: TabId;
    featureName: string;
    configPath: string;
  } | null>(null);

  // Sticky toolbar state
  const [showStickyToolbar, setShowStickyToolbar] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  // Queries
  const {
    data: agentConfig,
    isLoading,
    error,
    refetch,
  } = useAgentConfigQuery(agentType || '', !!agentType);

  const { data: modelsData } = useAllModelsQuery();

  // Query for versions to get the active version number
  const { data: versions } = useAgentVersionsQuery(agentType || '', !!agentType);

  // Get the active version from versions list
  const activeVersion = useMemo(() => {
    return versions?.find(v => v.is_active);
  }, [versions]);

  // Mutations
  const updateMutation = useUpdateAgentMutation();

  // Initialize form data and expanded tabs when config loads
  useEffect(() => {
    if (agentConfig?.configuration) {
      setFormData(agentConfig.configuration);
      setIsDirty(false);
      // Initialize expanded tabs based on which features are already enabled
      setExpandedTabs(getInitialExpandedTabs(agentConfig.configuration));
    }
  }, [agentConfig]);

  // Auto-expand tabs when features are enabled (even if not via handleFieldChange)
  // This handles cases like:
  // - JSON editing in versions tab
  // - Config synchronization from backend
  // - Copying configs from other agents
  useEffect(() => {
    if (!formData || Object.keys(formData).length === 0) return;

    setExpandedTabs(prev => {
      const next = new Set(prev);
      let changed = false;

      // Check each feature path and auto-expand if enabled
      Object.entries(FEATURE_PATH_TO_TAB).forEach(([path, tabId]) => {
        const value = getConfigValue(formData, path);
        // Auto-expand if feature is enabled but tab is not expanded
        if (value && !prev.has(tabId)) {
          next.add(tabId);
          changed = true;
        }
      });

      // Handle team mode (agent_mode === 'team')
      if (formData.agent_mode === 'team' && !prev.has('team')) {
        next.add('team');
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [formData]);

  // Sticky toolbar: show when header scrolls out of view
  // Note: agentConfig dependency ensures this runs after loading completes and header element exists
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    // Find the scrolling container (main element from MainLayout)
    const scrollContainer = header.closest('main');
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Get header's position relative to the scroll container
      const headerRect = header.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      // Show sticky toolbar when header top is above the container top
      const headerIsAbove = headerRect.bottom < containerRect.top + 10;
      setShowStickyToolbar(headerIsAbove);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    // Initial check
    handleScroll();

    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [agentConfig]);

  // Handle field change
  const handleFieldChange = useCallback((name: string, value: any) => {
    setFormData((prev) => {
      // Handle nested fields (e.g., memory.enabled)
      if (name.includes('.')) {
        const parts = name.split('.');
        const newData = { ...prev };
        let current: any = newData;

        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          } else {
            current[parts[i]] = { ...current[parts[i]] };
          }
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = value;
        return newData;
      }

      return { ...prev, [name]: value };
    });
    setIsDirty(true);

    // Auto-expand tab when a feature is enabled
    const tabId = FEATURE_PATH_TO_TAB[name];
    if (tabId && value) {
      setExpandedTabs(prev => {
        if (prev.has(tabId)) return prev;
        const next = new Set(prev);
        next.add(tabId);
        return next;
      });
    }

    // Handle agent_mode -> team tab
    if (name === 'agent_mode' && value === 'team') {
      setExpandedTabs(prev => {
        if (prev.has('team')) return prev;
        const next = new Set(prev);
        next.add('team');
        return next;
      });
    }
  }, []);

  // Handle save
  const handleSave = async () => {
    if (!agentType) return;

    try {
      // Clean up: if using new schema reference mode, clear deprecated embedded schemas
      const configToSave = { ...formData };
      const hasEnabledKeys = Array.isArray(configToSave.enabled_schema_keys) && configToSave.enabled_schema_keys.length > 0;
      if (hasEnabledKeys || configToSave.default_schema_key) {
        configToSave.output_schemas = []; // Clear deprecated embedded schemas
      }

      // Clean up skills: filter empty lines from patterns and example_queries
      if (configToSave.hooks?.classifier?.skills) {
        configToSave.hooks = {
          ...configToSave.hooks,
          classifier: {
            ...configToSave.hooks.classifier,
            skills: configToSave.hooks.classifier.skills.map(skill => ({
              ...skill,
              patterns: skill.patterns?.filter((p: string) => p.trim()) || [],
              example_queries: skill.example_queries?.filter((q: string) => q.trim()) || [],
            })),
          },
        };
      }

      // Clean up legacy: separate builtin tools from custom_tools array
      if (Array.isArray(configToSave.custom_tools)) {
        const builtinInCustom = configToSave.custom_tools.filter(
          (t: string) => typeof t === 'string' && t.startsWith('builtin:')
        );
        const pureCustomTools = configToSave.custom_tools.filter(
          (t: string) => typeof t !== 'string' || !t.startsWith('builtin:')
        );

        // Move builtin tools to builtin_tools field
        if (builtinInCustom.length > 0) {
          // Get existing builtin_tools config
          let existingToolkits: string[] = [];
          let existingEnabledTools: string[] = [];

          if (Array.isArray(configToSave.builtin_tools)) {
            existingToolkits = configToSave.builtin_tools;
          } else if (configToSave.builtin_tools && typeof configToSave.builtin_tools === 'object') {
            existingToolkits = configToSave.builtin_tools.toolkits || [];
            existingEnabledTools = configToSave.builtin_tools.enabled_tools || [];
          }

          // Merge and dedupe toolkits
          const mergedToolkits = [...new Set([...existingToolkits, ...builtinInCustom])];

          // Build new config preserving enabled_tools
          const newBuiltinConfig: { toolkits: string[]; enabled_tools?: string[] } = {
            toolkits: mergedToolkits,
          };
          if (existingEnabledTools.length > 0) {
            newBuiltinConfig.enabled_tools = existingEnabledTools;
          }

          configToSave.builtin_tools = newBuiltinConfig;

          // Update custom_tools
          configToSave.custom_tools = pureCustomTools.length > 0 ? pureCustomTools : null;

          console.log('[Save] Migrated builtin tools from custom_tools:', builtinInCustom);
        }
      }

      await updateMutation.mutateAsync({
        agentType,
        config: configToSave,
        description: t.updatedViaAdmin,
      });
      setIsDirty(false);
    } catch (err) {
      console.error('Failed to save:', err);
    }
  };

  // Handle tab navigation (from FeatureToggles)
  const handleNavigateToTab = useCallback((tabId: string) => {
    setActiveTab(tabId as TabId);
  }, []);

  // Handle tool browser
  const handleBrowseTools = (fieldName: string) => {
    let mode: BrowserMode = 'custom';
    if (fieldName === 'builtin_tools') {
      mode = 'builtin';
    } else if (fieldName === 'mcp_servers') {
      mode = 'mcp';
    }
    setToolBrowserMode(mode);
    setToolBrowserField(fieldName);
    setToolBrowserOpen(true);
  };

  const handleToolsApply = (config: string) => {
    try {
      // Empty config means clear the field (set to null)
      if (!config || config.trim() === '' || config.trim() === '[]') {
        handleFieldChange(toolBrowserField, null);
        setToolBrowserOpen(false);
        return;
      }

      const parsed = JSON.parse(config);

      // Also treat empty arrays as null
      if (Array.isArray(parsed) && parsed.length === 0) {
        handleFieldChange(toolBrowserField, null);
        setToolBrowserOpen(false);
        return;
      }

      // For custom_tools array, separate builtin tools immediately
      if (toolBrowserField === 'custom_tools' && Array.isArray(parsed)) {
        const builtinTools = parsed.filter(
          (t: string) => typeof t === 'string' && t.startsWith('builtin:')
        );
        const customTools = parsed.filter(
          (t: string) => typeof t !== 'string' || !t.startsWith('builtin:')
        );

        // If there are builtin tools, migrate them
        if (builtinTools.length > 0) {
          // Get existing builtin_tools config
          const existingBuiltin = formData.builtin_tools;
          let existingToolkits: string[] = [];
          let existingEnabledTools: string[] = [];

          if (Array.isArray(existingBuiltin)) {
            // Legacy array format
            existingToolkits = existingBuiltin;
          } else if (existingBuiltin && typeof existingBuiltin === 'object') {
            // New object format
            existingToolkits = existingBuiltin.toolkits || [];
            existingEnabledTools = existingBuiltin.enabled_tools || [];
          }

          // Merge and dedupe toolkits
          const mergedToolkits = [...new Set([...existingToolkits, ...builtinTools])];

          // Build new config preserving enabled_tools
          const newBuiltinConfig: { toolkits: string[]; enabled_tools?: string[] } = {
            toolkits: mergedToolkits,
          };
          if (existingEnabledTools.length > 0) {
            newBuiltinConfig.enabled_tools = existingEnabledTools;
          }

          // Update builtin_tools field
          handleFieldChange('builtin_tools', newBuiltinConfig);

          // Update custom_tools field (only non-builtin)
          handleFieldChange('custom_tools', customTools.length > 0 ? customTools : null);

          console.log('[ToolsApply] Migrated builtin tools:', builtinTools);
          setToolBrowserOpen(false);
          return;
        }

        // No builtin tools, just set custom_tools
        handleFieldChange(toolBrowserField, customTools.length > 0 ? customTools : null);
        setToolBrowserOpen(false);
        return;
      }

      // For object config, check if it's effectively empty
      if (typeof parsed === 'object' && parsed !== null) {
        const hasToolkits = parsed.toolkits?.length > 0;
        const hasServers = parsed.servers?.length > 0;
        const hasModules = parsed.modules?.length > 0;
        const hasEnabledTools = parsed.enabled_tools?.length > 0;
        if (!hasToolkits && !hasServers && !hasModules && !hasEnabledTools) {
          handleFieldChange(toolBrowserField, null);
        } else {
          handleFieldChange(toolBrowserField, parsed);
        }
      } else {
        handleFieldChange(toolBrowserField, parsed);
      }
    } catch (e) {
      console.error('Failed to parse tools config:', e);
      handleFieldChange(toolBrowserField, null);
    }
    setToolBrowserOpen(false);
  };

  // Group fields by section
  const groupedFields = useMemo(() => {
    const groups: Record<string, Array<{ name: string; schema: typeof FIELD_CONFIG[string] }>> = {};

    Object.entries(FIELD_CONFIG).forEach(([name, schema]) => {
      const section = schema.section;
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push({ name, schema });
    });

    return groups;
  }, []);

  // App context for field rendering (includes models)
  const appContext = useMemo(() => ({
    ...formData,
    chatModels: modelsData?.chat_models || [],
    memoryModels: modelsData?.memory_models || [],
    embeddingModels: modelsData?.embedding_models || [],
    visionModels: modelsData?.vision_models || [],
    reasoningModels: modelsData?.reasoning_models || [],
  }), [formData, modelsData]);

  // Handle toggle expansion for expandable feature tabs (扩展启用)
  const handleToggleExpansion = useCallback((tabId: TabId) => {
    const isExpanded = expandedTabs.has(tabId);

    // Expanding: always allow
    if (!isExpanded) {
      setExpandedTabs(prev => new Set(prev).add(tabId));
      return;
    }

    // Collapsing: check if feature is enabled
    const featureConfig = TAB_TO_FEATURE_CONFIG[tabId];
    if (!featureConfig) {
      // No associated feature, just collapse
      setExpandedTabs(prev => {
        const next = new Set(prev);
        next.delete(tabId);
        return next;
      });
      return;
    }

    // Check if feature is enabled
    const configValue = getConfigValue(formData, featureConfig.path);
    const isFeatureEnabled = tabId === 'team'
      ? configValue === 'team'  // Special handling for team mode
      : Boolean(configValue);

    if (isFeatureEnabled) {
      // Feature is enabled, show confirmation dialog
      setCollapseConfirmation({
        tabId,
        featureName: featureConfig.name,
        configPath: featureConfig.path,
      });
    } else {
      // Feature not enabled, just collapse
      setExpandedTabs(prev => {
        const next = new Set(prev);
        next.delete(tabId);
        return next;
      });
    }
  }, [expandedTabs, formData]);

  // Handle collapse confirmation - user confirmed to disable feature
  const handleConfirmCollapse = useCallback(() => {
    if (!collapseConfirmation) return;

    const { tabId, configPath } = collapseConfirmation;

    // Collapse the tab
    setExpandedTabs(prev => {
      const next = new Set(prev);
      next.delete(tabId);
      return next;
    });

    // Disable the feature
    const newValue = tabId === 'team' ? 'single' : false;
    handleFieldChange(configPath, newValue);

    // Also update hooks_visible for hooks that are linked to tabs
    const hookName = TAB_TO_HOOK_NAME[tabId];
    if (hookName) {
      handleFieldChange(`hooks_visible.${hookName}`, false);
      handleFieldChange(`hooks.${hookName}.enabled`, false);
    }

    // Close dialog
    setCollapseConfirmation(null);
  }, [collapseConfirmation, handleFieldChange]);

  // Handle collapse cancellation - user wants to keep tab open
  const handleCancelCollapse = useCallback(() => {
    setCollapseConfirmation(null);
  }, []);

  // Get visible tabs based on current config and expanded tabs
  const visibleTabs = useMemo(() => {
    return getVisibleTabs(formData, expandedTabs);
  }, [formData, expandedTabs]);

  // Get current field value
  const getFieldValue = (name: string) => {
    return getConfigValue(formData, name);
  };

  // Tab label mapping
  const getTabLabel = (tabId: TabId): string => {
    const labels: Record<TabId, string> = {
      basic: t.tabBasic || '基础',
      hooks: t.tabHooks || 'Hooks',
      memory: t.tabMemory || '记忆',
      knowledge: t.tabKnowledge || '知识库',
      astrology: t.tabAstrology || '占星',
      emotion: t.tabEmotion || '情绪感知',
      classifier: t.tabClassifier || '分类器',
      team: t.tabTeam || '团队',
      tools: t.tabTools || '工具',
      prompts: t.tabPrompt || '提示词',
      advanced: t.tabAdvanced || '高级',
      schemas: t.tabSchemas || 'Schemas',
      versions: t.tabVersions || '版本',
    };
    return labels[tabId];
  };

  if (isLoading) {
    return <Loading message={t.loadingConfig} />;
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage error={error.message} onRetry={() => refetch()} />
        <div className="mt-4">
          <Button variant="secondary" onClick={() => navigate('/')}>
            {t.backToList}
          </Button>
        </div>
      </div>
    );
  }

  if (!agentConfig) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <p className="text-slate-500">{t.agentNotFound}</p>
          <Button className="mt-4" onClick={() => navigate('/')}>
            {t.backToList}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Sticky Toolbar - appears when header scrolls out of view */}
      <div
        className={`sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm -mx-6 px-6 py-2 transition-all duration-200 ${
          showStickyToolbar ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none h-0 py-0 border-0 overflow-hidden'
        }`}
      >
        <div className="flex items-center justify-between">
          {/* Left: Back button + Breadcrumb */}
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => navigate('/')}
              className="text-slate-500 hover:text-slate-700 flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <span className="text-slate-400">/</span>
            <span className="text-slate-500 truncate max-w-[150px]">
              {agentConfig.configuration.name}
            </span>
            <span className="text-slate-300">/</span>
            <span className="font-medium text-slate-700">
              {getTabLabel(activeTab)}
            </span>
            <span className="text-slate-300 mx-1">|</span>
            <span className="text-slate-400 font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
              {agentType}
            </span>
            <span className="text-xs text-slate-400">
              v{activeVersion?.version || agentConfig.version}
            </span>
            <span className={`text-xs ${agentConfig.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
              {agentConfig.is_active ? t.active : t.inactive}
            </span>
          </div>

          {/* Right: Action buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCopyDialog(true)}
              title={t.copy}
            >
              <Copy className="w-4 h-4 mr-1" />
              {t.copy}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPromptPreview(true)}
              title={t.promptPreview?.title || '系统提示词预览'}
            >
              <Eye className="w-4 h-4 mr-1" />
              {t.promptPreview?.buttonLabel || '预览'}
            </Button>
            <span className="text-slate-200">|</span>
            {isDirty && (
              <span className="text-xs text-amber-600 px-2">{t.unsavedChanges}</span>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (agentConfig?.configuration) {
                  setFormData(agentConfig.configuration);
                  setIsDirty(false);
                  setExpandedTabs(getInitialExpandedTabs(agentConfig.configuration));
                }
              }}
              disabled={!isDirty}
            >
              {tc.reset}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? t.saving : ta.saveChanges}
            </Button>
          </div>
        </div>
      </div>

      {/* Header */}
      <div ref={headerRef} className="space-y-3">
        {/* Title Row */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="text-slate-500 hover:text-slate-700 flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-slate-900 truncate">
            {agentConfig.configuration.name}
          </h1>
          <span className="text-slate-500 font-mono text-xs bg-slate-100 px-2 py-0.5 rounded flex-shrink-0">
            {agentType}
          </span>
          <span className="text-slate-300 flex-shrink-0">|</span>
          <span className="text-xs text-slate-500 flex-shrink-0">
            v{activeVersion?.version || agentConfig.version}
          </span>
          <span className={`text-xs flex-shrink-0 ${agentConfig.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
            {agentConfig.is_active ? t.active : t.inactive}
          </span>
        </div>

        {/* Description Row (truncated) */}
        {agentConfig.configuration.description && (
          <p className="text-sm text-slate-500 line-clamp-2 ml-8" title={agentConfig.configuration.description}>
            {agentConfig.configuration.description}
          </p>
        )}

        {/* Action Buttons Row */}
        <div className="flex items-center justify-between ml-8">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>{t.updated}: {new Date(activeVersion?.updated_at || agentConfig.updated_at).toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCopyDialog(true)}
              title={t.copy}
            >
              <Copy className="w-4 h-4 mr-1" />
              {t.copy}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPromptPreview(true)}
              title={t.promptPreview?.title || '系统提示词预览'}
            >
              <Eye className="w-4 h-4 mr-1" />
              {t.promptPreview?.buttonLabel || '预览'}
            </Button>
            <span className="text-slate-200">|</span>
            {isDirty && (
              <span className="text-xs text-amber-600 px-2">{t.unsavedChanges}</span>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (agentConfig?.configuration) {
                  setFormData(agentConfig.configuration);
                  setIsDirty(false);
                  setExpandedTabs(getInitialExpandedTabs(agentConfig.configuration));
                }
              }}
              disabled={!isDirty}
            >
              {tc.reset}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? t.saving : ta.saveChanges}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
        <TabsList className="flex-wrap">
          {visibleTabs.map((tab) => {
            const Icon = TAB_ICONS[tab.id];
            return (
              <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                {getTabLabel(tab.id)}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Basic Tab */}
        <TabsContent value="basic" className="mt-6">
          <BasicTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
            onNavigateToSchemas={() => setActiveTab('schemas')}
          />
        </TabsContent>

        {/* Hooks Tab */}
        <TabsContent value="hooks" className="mt-6">
          <Card className="p-6">
            <HooksPipeline
              agentType={agentType || ''}
              localConfig={formData}
              onConfigChange={handleFieldChange}
              onNavigateToTab={handleNavigateToTab}
              expandedTabs={expandedTabs}
              onToggleExpansion={handleToggleExpansion}
            />
          </Card>
        </TabsContent>

        {/* Memory Tab (conditional) */}
        <TabsContent value="memory" className="mt-6">
          <MemoryTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
          />
        </TabsContent>

        {/* Knowledge Tab (conditional) */}
        <TabsContent value="knowledge" className="mt-6">
          <KnowledgeTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
          />
        </TabsContent>

        {/* Astrology Tab (conditional) */}
        <TabsContent value="astrology" className="mt-6">
          <AstrologyTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
          />
        </TabsContent>

        {/* Emotion Tab (conditional) */}
        <TabsContent value="emotion" className="mt-6">
          <EmotionTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
          />
        </TabsContent>

        {/* Classifier Tab (conditional) */}
        <TabsContent value="classifier" className="mt-6">
          <ClassifierTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
            agentType={agentType || ''}
          />
        </TabsContent>

        {/* Team Tab (conditional) */}
        <TabsContent value="team" className="mt-6">
          <TeamTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
          />
        </TabsContent>

        {/* Tools Tab */}
        <TabsContent value="tools" className="mt-6">
          <div className="space-y-6">
            {/* Tool Combo Section */}
            <ToolComboSection
              config={formData}
              onChange={handleFieldChange}
            />

            {/* Tool Configuration Sections */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {groupedFields['custom_tools'] && (
                <div className={FIELD_SECTIONS['custom_tools'].layout === 1 ? 'xl:col-span-2' : ''}>
                  <FieldSection
                    sectionKey="custom_tools"
                    sectionConfig={FIELD_SECTIONS['custom_tools']}
                    fields={groupedFields['custom_tools']}
                    values={appContext}
                    onChange={handleFieldChange}
                    appContext={appContext}
                    onBrowseTools={handleBrowseTools}
                  />
                </div>
              )}

              {groupedFields['builtin_tools'] && (
                <div className={FIELD_SECTIONS['builtin_tools'].layout === 1 ? 'xl:col-span-2' : ''}>
                  <FieldSection
                    sectionKey="builtin_tools"
                    sectionConfig={FIELD_SECTIONS['builtin_tools']}
                    fields={groupedFields['builtin_tools']}
                    values={appContext}
                    onChange={handleFieldChange}
                    appContext={appContext}
                    onBrowseTools={handleBrowseTools}
                  />
                </div>
              )}

              {groupedFields['mcp_tools'] && (
                <div className={FIELD_SECTIONS['mcp_tools'].layout === 1 ? 'xl:col-span-2' : ''}>
                  <FieldSection
                    sectionKey="mcp_tools"
                    sectionConfig={FIELD_SECTIONS['mcp_tools']}
                    fields={groupedFields['mcp_tools']}
                    values={appContext}
                    onChange={handleFieldChange}
                    appContext={appContext}
                    onBrowseTools={handleBrowseTools}
                  />
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Prompts Tab */}
        <TabsContent value="prompts" className="mt-6">
          <div className="space-y-6">
            {/* Classifier Warning */}
            {getConfigValue(formData, 'hooks.classifier.enabled') && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-amber-800">分类器已启用</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    当响应分类器启用时，系统提示词将由分类器动态控制。
                    如需修改系统指令，请前往分类器配置中的"快速响应指令"和"深度响应指令"进行设置。
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveTab('classifier')}
                    className="mt-2 text-sm text-amber-800 hover:text-amber-900 underline"
                  >
                    前往分类器配置 →
                  </button>
                </div>
              </div>
            )}

            {/* System Prompts Section */}
            {groupedFields['prompts'] && (
              <div className={getConfigValue(formData, 'hooks.classifier.enabled') ? 'opacity-50 pointer-events-none' : ''}>
                <FieldSection
                  sectionKey="prompts"
                  sectionConfig={FIELD_SECTIONS['prompts']}
                  fields={groupedFields['prompts']}
                  values={appContext}
                  onChange={handleFieldChange}
                  appContext={appContext}
                />
              </div>
            )}

            {/* Template Selector */}
            <TemplateSelector
              value={formData.prompt_template_id ?? null}
              onChange={(templateId) => handleFieldChange('prompt_template_id', templateId)}
            />
          </div>
        </TabsContent>

        {/* Advanced Tab */}
        <TabsContent value="advanced" className="mt-6">
          <AdvancedTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
          />
        </TabsContent>

        {/* Schemas Tab */}
        <TabsContent value="schemas" className="mt-6">
          <OutputSchemasTab
            enabledSchemaKeys={formData.enabled_schema_keys || []}
            defaultSchemaKey={formData.default_schema_key || null}
            onEnabledKeysChange={(keys) => handleFieldChange('enabled_schema_keys', keys)}
            onDefaultKeyChange={(key) => handleFieldChange('default_schema_key', key)}
          />
        </TabsContent>

        {/* Versions Tab (includes JSON preview) */}
        <TabsContent value="versions" className="mt-6">
          <VersionHistory
            agentType={agentType || ''}
            currentVersion={agentConfig.version}
            currentConfig={formData}
            onConfigReplace={(newConfig) => {
              setFormData(newConfig as AgentConfiguration);
              setIsDirty(true);
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Tool Browser Modal */}
      {toolBrowserMode !== 'mcp' && (
        <ToolsBrowser
          isOpen={toolBrowserOpen}
          mode={toolBrowserMode as ToolMode}
          initialConfig={getFieldValue(toolBrowserField)}
          onClose={() => setToolBrowserOpen(false)}
          onApply={handleToolsApply}
        />
      )}
      {toolBrowserMode === 'mcp' && (
        <MCPToolsBrowser
          isOpen={toolBrowserOpen}
          initialConfig={getFieldValue(toolBrowserField)}
          onClose={() => setToolBrowserOpen(false)}
          onApply={handleToolsApply}
        />
      )}

      {/* Copy Agent Dialog */}
      <CreateAgentDialog
        isOpen={showCopyDialog}
        onClose={() => setShowCopyDialog(false)}
        onSuccess={(newAgentType) => navigate(`/agents/${newAgentType}`)}
        copyFrom={{
          agentType: agentType || '',
          config: formData,
        }}
      />

      {/* Prompt Preview Dialog */}
      {showPromptPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowPromptPreview(false)}
          />

          {/* Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-auto">
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-slate-200 bg-white">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary-600" />
                <h2 className="text-lg font-semibold text-slate-900">
                  {t.promptPreview?.title || '系统提示词预览'}
                </h2>
              </div>
              <button
                onClick={() => setShowPromptPreview(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <AgentPromptPreview
                templateId={formData.prompt_template_id ?? null}
                agentType={agentType || ''}
              />
            </div>
          </div>
        </div>
      )}

      {/* Collapse Confirmation Dialog */}
      {collapseConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop - no click handler to prevent accidental dismissal */}
          <div className="absolute inset-0 bg-black/50" />

          {/* Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center gap-3 p-6 border-b border-slate-200">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                确认收起配置
              </h2>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-slate-600">
                <span className="font-medium text-slate-900">{collapseConfirmation.featureName}</span>
                {' '}当前已启用。
              </p>
              <p className="text-slate-600">
                收起配置Tab后，该功能将被禁用。是否继续？
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50">
              <Button
                variant="ghost"
                onClick={handleCancelCollapse}
              >
                取消
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmCollapse}
              >
                继续收起并禁用
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {updateMutation.isError && (
        <div className="fixed top-20 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {t.saveFailed}: {(updateMutation.error as Error).message}
        </div>
      )}

      {/* Success Toast */}
      {updateMutation.isSuccess && (
        <div className="fixed top-20 right-4 bg-emerald-500 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {t.saveSuccess}
        </div>
      )}
    </div>
  );
};

export default AgentConfigPage;
